import React, { useEffect } from 'react';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { getNextgenSettingsIssueTypesUrl } from '@atlassian/jira-project-settings-apps-common/src/urls.tsx';
import {
	INIT,
	FAILURE,
	SUCCESS,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/constants.tsx';
import type { ProductFeatures } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types.tsx';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { Redirect } from '@atlassian/react-resource-router';
import {
	useIssueTypesNavigationItems,
	useIssueTypesNavigationItemsActions,
} from '../../../controllers/issue-types-navigation-items/index.tsx';
import messages from './messages.tsx';

type IssueTypesNavigationItemsFetcherProps = {
	projectId: number;
	productFeatures: ProductFeatures;
};

export const IssueTypesNavigationItemsFetcher = ({
	projectId,
	productFeatures,
}: IssueTypesNavigationItemsFetcherProps) => {
	const actions = useIssueTypesNavigationItemsActions();
	const [{ asyncState, projectId: currentProjectId }] = useIssueTypesNavigationItems();
	const intl = useIntl();

	useEffect(() => {
		if (asyncState === INIT || projectId !== currentProjectId) {
			actions.load(projectId, productFeatures, intl);
		}
	}, [asyncState, actions, projectId, currentProjectId, productFeatures, intl]);

	return null;
};

type RedirectToDefaultIssueTypeProps = {
	project: ProjectContext;
	extraDefinerId: number | null;
};

const getBlankAndGeneralExpCohort = () => {
	// Don't expose this experiment here because this is just a util. Seeing the template is handled elsewhere.
	const [expConfig] = UNSAFE_noExposureExp('jsm_remove_blank_and_general_template_demarcation');

	return expConfig.get('cohort', 'not-enrolled');
};

export const RedirectToDefaultIssueType = ({
	extraDefinerId,
	project,
}: RedirectToDefaultIssueTypeProps) => {
	const [{ asyncState, baseLevel, topLevel, bottomLevel }] = useIssueTypesNavigationItems();
	const { showFlag } = useFlagsService();

	useEffect(() => {
		if (asyncState === FAILURE) {
			showFlag({
				type: 'error',
				title: messages.errorTitle,
				description: fg('jira-issue-terminology-refresh-m3')
					? messages.errorDescriptionIssueTermRefresh
					: messages.errorDescription,
				messageId: 'spa-apps-project-settings-issue-type.team-managed.navigation.show-flag.error',
				messageType: 'transactional',
			});
		}
	}, [asyncState, showFlag]);

	if (asyncState === FAILURE) {
		return null;
	}

	const isKnownIssueType = [...topLevel, ...baseLevel, ...bottomLevel].some(
		({ id }) => id === extraDefinerId,
	);

	if (asyncState !== SUCCESS || isKnownIssueType) {
		return null;
	}

	let defaultIssueTypeId = '';
	if (getBlankAndGeneralExpCohort() === 'variation') {
		defaultIssueTypeId = baseLevel[0] ? String(baseLevel[0].id) : '';
		if (!defaultIssueTypeId) {
			defaultIssueTypeId = topLevel[0] ? String(topLevel[0].id) : ''; // Fallback if no base level issue type but top level exists
		}
	} else {
		defaultIssueTypeId = baseLevel[0] ? String(baseLevel[0].id) : '';
	}

	if (!defaultIssueTypeId) {
		return null;
	}

	return (
		<Redirect
			push={false}
			to={getNextgenSettingsIssueTypesUrl(
				project.projectType,
				project.projectKey,
				defaultIssueTypeId,
			)}
		/>
	);
};
